import { useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Archive, BookOpen, Briefcase, FileText, Flag, HardDrive, Inbox, KeyRound, LogOut, Mail, RefreshCw, Search, ShieldCheck, Users } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "../components/brand/Logo";
import { useAuth } from "../components/admin/AuthContext";
import { EnquiryDetail, STATUS, TYPE_LABEL } from "../components/admin/EnquiryDetail";
import { AiConcierge } from "../components/admin/AiConcierge";
import { ChangePassword } from "../components/admin/ChangePassword";
import { AuditLog, LegalDrafts, Retention, StaffAdmin } from "../components/admin/Governance";
import { Backups } from "../components/admin/Mfa";
import { CompanyDocs } from "../components/admin/CompanyDocs";
import { LaunchDashboard } from "../components/admin/LaunchDashboard";
import { api, formatApiError } from "../lib/api";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-KE", { dateStyle: "medium", timeStyle: "short" }) : "");

const Stat = ({ label, value, id }) => (
  <div className="rounded-2xl border border-white/10 bg-[#141110] p-4" data-testid={`admin-stat-${id}`}>
    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-400">{label}</p>
    <p className="mt-2 font-display text-2xl text-stone-50">{value ?? 0}</p>
  </div>
);

const Subscribers = ({ items }) => (
  <div className="overflow-hidden rounded-2xl border border-white/10" data-testid="admin-subscribers">
    {items.length === 0 && <p className="p-8 text-center text-sm text-stone-400">No Care-Shop sign-ups yet.</p>}
    {items.map((s) => (
      <div key={s.id} className="flex flex-col gap-1 border-b border-white/10 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <span className="text-stone-100">{s.email}</span>
        <span className="text-xs text-stone-400">{(s.interests ?? []).join(", ") || "—"} · {fmt(s.created_at)}</span>
      </div>
    ))}
  </div>
);

const EmailBanner = ({ s }) => {
  if (!s) return null;
  const live = s.enabled && s.provider_ready;
  return (
    <div data-testid="admin-email-status"
      className={`mb-6 rounded-2xl border px-5 py-4 text-sm ${live ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-100" : "border-amber-400/30 bg-amber-400/10 text-amber-100"}`}>
      <strong className="font-semibold">Email notifications: {live ? "active" : "disabled"}.</strong>{" "}
      {live
        ? `Team recipients: ${s.team_recipients}. Customer acknowledgements: ${s.customer_acknowledgement ? "on" : "off"}.`
        : "Enquiries are still saved here. Outbound email starts once team recipient addresses are configured and email is enabled on the server."}
    </div>
  );
};

export default function AdminInbox() {
  const { user, logout, can } = useAuth();
  const canEnq = can("enquiries.view");
  const [tab, setTab] = useState(canEnq ? "enquiries" : "ai");
  const [pwOpen, setPwOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [subs, setSubs] = useState([]);
  const [stats, setStats] = useState(null);
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [scope, setScope] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [opening, setOpening] = useState(null);
  const [emailStatus, setEmailStatus] = useState(null);
  const latestSeen = useRef(null);

  const load = useCallback(async () => {
    if (!canEnq) return;
    try {
      const [e, s, st] = await Promise.all([
        api.get("/admin/enquiries", { params: { status: status || undefined, type: type || undefined, scope: scope || undefined, q: q || undefined } }),
        can("subscribers.view") ? api.get("/admin/subscribers") : Promise.resolve({ data: [] }),
        api.get("/admin/stats"),
      ]);
      setItems(Array.isArray(e.data) ? e.data : []);
      setSubs(Array.isArray(s.data) ? s.data : []);
      setStats(st.data ?? null);
      latestSeen.current = st.data?.latest_created_at ?? latestSeen.current;
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, [status, type, scope, q, canEnq, can]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!canEnq) return;
    api.get("/admin/email-status").then((r) => setEmailStatus(r.data ?? null)).catch(() => {});
  }, [canEnq]);

  useEffect(() => {
    if (!canEnq) return undefined;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get("/admin/stats");
        if (data?.latest_created_at && latestSeen.current && data.latest_created_at > latestSeen.current) {
          toast.success("New enquiry received", { description: "The inbox has been refreshed." });
          load();
        }
        setStats(data ?? null);
      } catch { /* session errors surface on next action */ }
    }, 30000);
    return () => clearInterval(t);
  }, [load, canEnq]);

  const open = async (x) => {
    setOpening(x.id);
    try {
      const { data } = x.read === false
        ? await api.post(`/admin/enquiries/${x.id}/read`)
        : await api.get(`/admin/enquiries/${x.id}`);
      if (x.read === false) onUpdated(data);
      else setSelected(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setOpening(null);
    }
  };

  const onUpdated = (u) => {
    const leaves = Boolean(u.archived) !== (scope === "archived");
    setItems((xs) => (leaves ? xs.filter((x) => x.id !== u.id) : xs.map((x) => (x.id === u.id ? u : x))));
    setSelected(leaves ? null : u);
    api.get("/admin/stats").then((r) => setStats(r.data ?? null)).catch(() => {});
  };

  return (
    <div className="min-h-screen bg-[#0C0A09] text-stone-100" data-testid="admin-inbox">
      <Helmet><title>Inbox | TechCare Admin</title><meta name="robots" content="noindex, nofollow" /></Helmet>
      <header className="sticky top-0 z-20 border-b border-white/10 bg-[#0C0A09]/90 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-[1440px] items-center justify-between px-5 sm:px-8">
          <Logo className="h-11 w-auto" glow={false} />
          <div className="flex items-center gap-3 text-sm">
            <span className="hidden text-stone-400 sm:inline" data-testid="admin-user-role">{user?.email} · {user?.role_label}</span>
            <button onClick={() => setPwOpen((o) => !o)} className="inline-flex h-9 items-center gap-2 rounded-full border border-white/15 px-4 text-stone-200 hover:border-orange-500" data-testid="admin-change-password-btn">
              <KeyRound className="h-4 w-4" /> <span className="hidden sm:inline">Password</span>
            </button>
            <button onClick={logout} className="inline-flex h-9 items-center gap-2 rounded-full border border-white/15 px-4 text-stone-200 hover:border-orange-500" data-testid="admin-logout-btn">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-[1440px] px-5 py-8 sm:px-8">
        {pwOpen && <div className="mb-6 max-w-md"><ChangePassword onDone={() => setPwOpen(false)} /></div>}
        {canEnq && <EmailBanner s={emailStatus} />}
        {canEnq && <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-9">
          <Stat id="total" label="All enquiries" value={stats?.total} />
          <Stat id="unread" label="Unread" value={stats?.unread} />
          {Object.entries(STATUS).map(([k, v]) => <Stat key={k} id={k} label={v.label} value={stats?.enquiries?.[k]} />)}
          <Stat id="followups" label="Follow-ups due" value={stats?.follow_ups_due} />
          <Stat id="subscribers" label="Shop sign-ups" value={stats?.subscribers} />
          <Stat id="archived" label="Archived" value={stats?.archived} />
        </div>}

        <div className="mt-8 flex flex-wrap items-center gap-2">
          {[["enquiries", "Enquiries", Inbox, "enquiries.view"], ["subscribers", "Care-Shop list", Mail, "subscribers.view"], ["ai", "AI Concierge", BookOpen, "ai.view"],
            ["staff", "Staff", Users, "staff.manage"], ["audit", "Audit log", ShieldCheck, "audit.view"], ["retention", "Retention", Archive, "retention.view"],
            ["legal", "Legal drafts", FileText, "legal.view"], ["company", "Company docs", Briefcase, "legal.view"], ["backups", "Backups", HardDrive, "backup.manage"], ["launch", "Launch", Flag, "launch.manage"]].filter(([, , , p]) => can(p) || (p === "ai.view" && can("kb.view"))).map(([k, l, I]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
              className={`inline-flex h-10 items-center gap-2 rounded-full px-4 text-sm ${tab === k ? "bg-orange-500 text-black" : "border border-white/15 text-stone-300"}`}>
              <I className="h-4 w-4" /> {l}
            </button>
          ))}
          <button onClick={load} className="ml-auto inline-flex h-10 items-center gap-2 rounded-full border border-white/15 px-4 text-sm text-stone-300" data-testid="admin-refresh-btn">
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
        </div>

        {tab === "launch" ? <LaunchDashboard /> : tab === "backups" ? <Backups /> : tab === "staff" ? <StaffAdmin /> : tab === "audit" ? <AuditLog /> : tab === "retention" ? <Retention /> : tab === "legal" ? <LegalDrafts /> : tab === "company" ? <CompanyDocs /> :
          tab === "ai" ? <AiConcierge /> : tab === "subscribers" ? <div className="mt-6"><Subscribers items={subs} /></div> : (
          <>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <label className="relative flex-1">
                <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-500" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search reference, customer, organisation or service"
                  className="h-11 w-full rounded-full border border-white/10 bg-[#141110] pl-11 pr-4 text-sm outline-none focus:border-orange-500" data-testid="admin-search-input" />
              </label>
              <select value={type} onChange={(e) => setType(e.target.value)} aria-label="Filter by category"
                className="h-11 rounded-full border border-white/10 bg-[#141110] px-4 text-sm text-stone-200 outline-none" data-testid="admin-type-filter">
                <option value="">All categories</option>
                {Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Filter by status"
                className="h-11 rounded-full border border-white/10 bg-[#141110] px-4 text-sm text-stone-200 outline-none" data-testid="admin-status-filter">
                <option value="">All statuses</option>
                {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label="Quick filters">
              {[["", "All"], ["unread", "Unread"], ["service", "Service-specific"], ["follow_up", "With follow-up"], ["archived", "Archived"]].map(([k, l]) => (
                <button key={k || "all"} onClick={() => setScope(k)} aria-pressed={scope === k} data-testid={`admin-scope-${k || "all"}`}
                  className={`h-8 rounded-full px-3.5 text-xs ${scope === k ? "bg-white text-black" : "border border-white/15 text-stone-300 hover:border-orange-500"}`}>{l}</button>
              ))}
            </div>
            <div className="mt-6 grid gap-6 lg:grid-cols-12">
              <ul className="overflow-hidden rounded-2xl border border-white/10 lg:col-span-5" data-testid="admin-enquiry-list">
                {items.length === 0 && <li className="p-8 text-center text-sm text-stone-400">No enquiries match.</li>}
                {items.map((x) => (
                  <li key={x.id}>
                    <button onClick={() => open(x)} data-testid={`admin-enquiry-${x.reference}`} aria-busy={opening === x.id}
                      className={`block w-full border-b border-white/10 px-5 py-4 text-left transition-colors ${selected?.id === x.id || opening === x.id ? "bg-orange-500/10" : "hover:bg-white/5"}`}>
                      <span className="flex items-center justify-between gap-3">
                        <span className="flex items-center gap-2 font-mono text-xs text-orange-400">
                          {x.read === false && <span className="h-2 w-2 rounded-full bg-orange-500" aria-label="Unread" />}{x.reference}
                        </span>
                        <span className={`rounded-full border px-2 py-0.5 text-[10px] uppercase ${STATUS[x.status]?.cls}`}>{STATUS[x.status]?.label}</span>
                      </span>
                      <span className={`mt-2 block text-sm ${x.read === false ? "font-semibold text-white" : "text-stone-100"}`}>{x.name} {x.organisation ? `· ${x.organisation}` : ""}</span>
                      <span className="mt-1 block text-xs text-stone-400">
                        {TYPE_LABEL[x.type]}{x.service_specific ? " · Service page" : ""} · {fmt(x.created_at)}
                        {x.follow_up_at ? <span className="text-amber-300"> · Follow up {x.follow_up_at}</span> : null}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              <div className="lg:col-span-7">
                {selected ? <EnquiryDetail key={selected.id} item={selected} onUpdated={onUpdated} fmt={fmt} /> : (
                  <div className="grid h-full min-h-[240px] place-items-center rounded-2xl border border-dashed border-white/10 text-sm text-stone-400">Select an enquiry to view details</div>
                )}
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
